import * as XLSX from "xlsx";
import { z } from "zod";

// Column names of the phrase corpus files, as the backend reads and writes them.
export const PHRASE_COLUMNS = {
  local: "Phrase en langue nationale",
  fr: "Phrase équivalente en français",
  source: "source",
  domain: "Domain (optional)",
  lot: "Lot (optional)",
  curationOrder: "Curation order (optional)",
  id: "Phrase ID",
} as const;

export interface WorkbookPhrase {
  /**
   * The number of the row in the document, the first row being 1. It is the
   * same number the server publishes as `fileOrder`, so a row found in the
   * browser and a row found on the server are the same record.
   */
  rowNumber: number;
  id?: string;
  local: string;
  fr: string;
  source: string;
  domain: string;
  lot?: string;
  curationOrder?: number;
  fileOrder?: number;
}

const cell = (row: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

/** Rows of the first sheet. Also accepts short headers (local, french/fr). */
export function readPhraseWorkbook(data: ArrayBuffer): WorkbookPhrase[] {
  const workbook = XLSX.read(data, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  const seenIds = new Set<string>();
  return XLSX.utils
    .sheet_to_json<Record<string, unknown>>(sheet, { defval: "" })
    .map((row, index) => {
      const id = cell(row, PHRASE_COLUMNS.id);
      if (id && (!z.string().uuid().safeParse(id).success || seenIds.has(id))) {
        throw new Error("PHRASE_ID_INVALID_OR_DUPLICATE");
      }
      if (id) seenIds.add(id);
      const curationOrder = parseCurationOrder(
        row[PHRASE_COLUMNS.curationOrder],
      );
      return {
        ...(id ? { id } : {}),
        rowNumber: index + 1,
        local: cell(
          row,
          PHRASE_COLUMNS.local,
          "Phrase en langue national",
          "local",
        ),
        fr: cell(
          row,
          PHRASE_COLUMNS.fr,
          "Phrase equivalente en Francais",
          "french",
          "fr",
        ),
        source: cell(row, PHRASE_COLUMNS.source),
        domain: cell(row, PHRASE_COLUMNS.domain, "domain"),
        lot: cell(row, PHRASE_COLUMNS.lot),
        ...(curationOrder === undefined ? {} : { curationOrder }),
      };
    })
    .filter((row) => row.local || row.fr);
}

function parseCurationOrder(value: unknown): number | undefined {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return undefined;
  }
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < 1 ||
    value > 5000
  ) {
    throw new Error("PHRASE_CURATION_ORDER_INVALID");
  }
  return value;
}

export function buildPhraseWorkbook(
  rows: WorkbookPhrase[],
  fileName: string,
): File {
  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row) => ({
      [PHRASE_COLUMNS.local]: row.local,
      [PHRASE_COLUMNS.fr]: row.fr,
      [PHRASE_COLUMNS.source]: row.source,
      [PHRASE_COLUMNS.domain]: row.domain,
      [PHRASE_COLUMNS.lot]: row.lot ?? "",
      [PHRASE_COLUMNS.curationOrder]: row.curationOrder ?? "",
      [PHRASE_COLUMNS.id]: row.id ?? "",
    })),
    { header: Object.values(PHRASE_COLUMNS) },
  );
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Feuille1");
  const data = XLSX.write(workbook, {
    type: "array",
    bookType: "xlsx",
  }) as ArrayBuffer;
  return new File([data], fileName, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
